import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import '../css/HomeLayout.css';
import { useUser } from './userContext';
import { authAPI, tripAPI } from '../api/apiClient';
import AddTripPopup from './AddTripPopup';
import { homeMenuItems } from '../config/homeMenuItems';
import * as FaIcons from 'react-icons/fa';
import { FaArrowLeft, FaPowerOff } from 'react-icons/fa';

const HomeLayout = () => {
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { guid: tripGuid } = useParams();
  const [showAddTripPopup, setShowAddTripPopup] = useState(false);
  const [sidebarTrip, setSidebarTrip] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!tripGuid) { setSidebarTrip(null); return undefined; }
    tripAPI.getTrip(tripGuid).then((response) => { if (!cancelled) setSidebarTrip(response.data?.trip || response.data || null); }).catch(() => { if (!cancelled) setSidebarTrip(null); });
    return () => { cancelled = true; };
  }, [tripGuid]);

  const filteredMenuItems = useMemo(() => homeMenuItems.filter(item =>
    item.path !== 'popup:add-trip' && item.roles.some(role => user?.roles?.includes(role))
  ), [user]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Nie udało się wylogować. Spróbuj ponownie.');
    }
  };

  const handleGoBack = () => {
    const isRootTripPage = location.pathname === `/trips/${tripGuid}`;

    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else if (isRootTripPage) navigate('/', { replace: true });
    else if (tripGuid) navigate(`/trips/${tripGuid}`, { replace: true });
    else navigate('/');
  };

  const handleMenuClick = (item) => {
    if (item.path === 'popup:add-trip') {
      setShowAddTripPopup(true);
      return;
    }
    navigate(item.path);
  };

  const isOnHome = location.pathname === '/';
  const isTripsSection = isOnHome || location.pathname.startsWith('/trips/');
  const isUsersSection = location.pathname.startsWith('/users');
  const isViewsSection = location.pathname.startsWith('/views');
  const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || '?';
  const tripPathParts = location.pathname.split('/').filter(Boolean);
  const reservationIndex = tripPathParts.indexOf('registrations');
  const participantIndex = tripPathParts.indexOf('participants');
  const variantsIndex = tripPathParts.indexOf('variants');
  const sidebarVariantGuid = variantsIndex >= 0 ? tripPathParts[variantsIndex + 1] : null;
  const sidebarVariant = sidebarVariantGuid ? sidebarTrip?.variants?.find(v => v.guid === sidebarVariantGuid) : null;
  const sidebarReservationGuid = reservationIndex >= 0 ? tripPathParts[reservationIndex + 1] : null;
  const sidebarReservation = sidebarReservationGuid ? sidebarTrip?.registrations?.find(r => r.reservationGuid === sidebarReservationGuid) : null;
  const sidebarSectionKey = !sidebarReservationGuid && participantIndex < 0 ? tripPathParts.find(p => ['registrations','participants','discord','insurances','access','summary'].includes(p)) : null;
  const sidebarSectionLabels = { registrations:'Rejestracje', participants:'Uczestnicy', discord:'Discord', insurances:'Ubezpieczenia', access:'Dostęp', summary:'Zestawienia' };
  const sidebarLeaf = sidebarReservation ? `Rezerwacja ${sidebarReservation.reservationCode || ''}`.trim() : (participantIndex >= 0 ? 'Uczestnicy' : sidebarSectionLabels[sidebarSectionKey]);

  const isMenuItemActive = (item) => {
    if (item.path === '/users') return isUsersSection;
    if (item.path === '/views') return isViewsSection;
    return false;
  };

  return (
    <div className="layout-container">
      <div className="app-layout-grid">
        <aside className="app-sidebar">
          <div className="app-sidebar-brand" onClick={() => navigate('/')}>
            <img className="app-brand-logo" src="/td-sidebar-logo.png" alt="Logo Tęczowej Drużyny" />
            <span className="app-brand-separator" aria-hidden="true" />
            <div className="app-brand-name">CENTRUM<br />DOWODZENIA</div>
          </div>

          <nav className="app-sidebar-nav" aria-label="Główna nawigacja aplikacji">
            <button type="button" className={`app-sidebar-link ${isTripsSection ? 'app-sidebar-link--active' : ''}`} onClick={() => navigate('/')}>
              <FaIcons.FaRoute />
              <span>Wycieczki</span>
            </button>
            {tripGuid && sidebarTrip && (
              <div className="app-sidebar-context">
                <button type="button" className="app-sidebar-context-item app-sidebar-context-trip" onClick={() => navigate(`/trips/${tripGuid}`)}>
                  <span className="app-sidebar-tree-line">└─</span><span>{sidebarTrip.name}</span>
                </button>
                {sidebarVariant && <button type="button" className="app-sidebar-context-item app-sidebar-context-variant" onClick={() => navigate(`/trips/${tripGuid}/variants/${sidebarVariant.guid}`)}><span className="app-sidebar-tree-line">└─</span><span>{sidebarVariant.name}</span></button>}
                {sidebarLeaf && <div className="app-sidebar-context-item app-sidebar-context-leaf app-sidebar-context-depth-2"><span className="app-sidebar-tree-line">└─</span><span>{sidebarLeaf}</span></div>}
              </div>
            )}

            {filteredMenuItems.map((item) => {
              const Icon = FaIcons[item.icon];
              return (
                <button key={item.path} type="button" className={`app-sidebar-link ${isMenuItemActive(item) ? 'app-sidebar-link--active' : ''}`} onClick={() => handleMenuClick(item)}>
                  <Icon />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="app-sidebar-footer">
            <div className="app-sidebar-user">
              <div className="app-sidebar-avatar">
                {user?.picture ? <img src={user.picture} alt="" /> : <span>{userInitial}</span>}
              </div>
              <div className="app-sidebar-user-copy">
                <strong>{user?.name || 'Użytkownik'}</strong>
                <span>Zalogowano</span>
              </div>
            </div>
            <button type="button" className="app-sidebar-logout" onClick={handleLogout} title="Wyloguj się">
              <FaPowerOff />
            </button>
          </div>
        </aside>

        <div className="app-main-column">
          {!isOnHome && (
            <header className={`banner sticky-banner ${process.env.REACT_APP_ENV === 'test' ? 'banner--test' : ''}`}>
              {process.env.REACT_APP_ENV === 'test' && <div className="env-label">TEST</div>}
              <button className="back-nav" onClick={handleGoBack}><FaArrowLeft /></button>
              <div className="banner-right">
                <div className="user-info">
                  {user?.picture ? (
                    <img src={user.picture} alt="User Avatar" className="user-avatar" />
                  ) : (
                    <div className="user-avatar-initial user-avatar">{userInitial}</div>
                  )}
                  <p className="user-name">{user?.name || 'Gość'}</p>
                </div>
                <div className="logout-icon" onClick={handleLogout} title="Wyloguj się"><FaPowerOff /></div>
              </div>
            </header>
          )}

          <main className={`layout-content ${isOnHome ? 'layout-content--home' : ''}`}>
            <Outlet />
          </main>
        </div>
      </div>

      <AddTripPopup show={showAddTripPopup} onClose={() => setShowAddTripPopup(false)} />
    </div>
  );
};

export default HomeLayout;
